import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { FC, useCallback, useEffect } from 'react';
import { useTheme } from 'styled-components';

import { Translate } from '@core/constant';
import { useContextMediaQuery } from '@core/context';
import {
  ColorProps,
  EColor,
  EFontWeight,
  EPosition,
  ETypographyVariant,
  TextColorType,
  ThemeProps,
} from '@core/type';
import { getPaletteHandlers } from '@core/util';

import { IconCheck } from '../icon/IconCheck';
import { Box } from '../layout/Box';
import { Typography } from '../typography/Typography';
import { Tooltip } from './Tooltip';
import {
  CheckPasswordContentProps,
  CheckRuleProps,
  TooltipCheckPasswordProps,
} from './interface-tooltip';

const CheckRule: FC<CheckRuleProps> = ({ color, title }): JSX.Element => {
  const { palette } = useTheme() as ThemeProps;
  const { getTextColor } = getPaletteHandlers(palette);

  return (
    <Box
      display="flex"
      flex={'1 24px'}
      alignContent="center"
      justifyContent={'center'}
      gap={'10px'}
    >
      <IconCheck flex={'0 1'} stroke={getTextColor(color)} />
      <Typography
        flex={'1 1 0'}
        variant={ETypographyVariant.CAPTION1}
        color={color}
        marginBottom={'4px'}
      >
        {title}
      </Typography>
    </Box>
  );
};

const getColor = (isValid?: boolean): ColorProps<TextColorType> => {
  return isValid ? EColor.SUCCESS : EColor.PRIMARY;
};

export const CheckPasswordContent: FC<CheckPasswordContentProps> = ({
  isValidCharacters,
  isValidLetter,
  isValidCapital,
  isValidNumber,
  isValidSymbols,
}: CheckPasswordContentProps): JSX.Element => {
  const { t } = useTranslation(Translate.common.FORM);

  const charactersCheckColor = useCallback(() => getColor(isValidCharacters), [isValidCharacters]);
  const letterCheckColor = useCallback(() => getColor(isValidLetter), [isValidLetter]);
  const capitalCheckColor = useCallback(() => getColor(isValidCapital), [isValidCapital]);
  const numberCheckColor = useCallback(() => getColor(isValidNumber), [isValidNumber]);
  const symbolsCheckColor = useCallback(() => getColor(isValidSymbols), [isValidSymbols]);

  return (
    <Box textAlign={'left'}>
      <Box>
        <Typography
          variant={ETypographyVariant.CAPTION1}
          fontWeight={EFontWeight.MEDIUM}
          marginBottom={'8px'}
        >
          {t('password.tooltip.title')}
        </Typography>
      </Box>
      <CheckRule
        isValid={isValidCharacters}
        color={charactersCheckColor()}
        title={t('password.tooltip.characters')}
      />
      <CheckRule
        isValid={isValidLetter}
        color={letterCheckColor()}
        title={t('password.tooltip.letter')}
      />
      <CheckRule
        isValid={isValidCapital}
        color={capitalCheckColor()}
        title={t('password.tooltip.capital')}
      />
      <CheckRule
        isValid={isValidNumber}
        color={numberCheckColor()}
        title={t('password.tooltip.number')}
      />
      <CheckRule
        isValid={isValidSymbols}
        color={symbolsCheckColor()}
        title={
          <Trans
            i18nKey={`${Translate.common.FORM}:password.tooltip.symbols`}
            components={{ br: <br /> }}
          />
        }
      />
    </Box>
  );
};

export const TooltipCheckPassword: FC<TooltipCheckPasswordProps> = ({
  children,
  password,
  checkPassword,
}: TooltipCheckPasswordProps): JSX.Element => {
  const { isDesktop } = useContextMediaQuery();
  const isValidCharacters = useCallback(() => password && !!/^.{8,}$/.exec(password), [password]);
  const isValidLetter = useCallback(() => password && !!/[a-z]/.exec(password), [password]);
  const isValidCapital = useCallback(() => password && !!/[A-Z]/.exec(password), [password]);
  const isValidNumber = useCallback(() => password && !!/\d/.exec(password), [password]);
  const isValidSymbols = useCallback(
    () => password && !!/[-$^&+,:;=?@#|'"<>*()%!_`{}~./[\]]/.exec(password),
    [password],
  );

  useEffect(() => {
    if (checkPassword) {
      checkPassword(
        isValidCharacters() &&
          isValidLetter() &&
          isValidCapital() &&
          isValidNumber() &&
          isValidSymbols(),
      );
    }
  }, [
    checkPassword,
    isValidCharacters,
    isValidLetter,
    isValidCapital,
    isValidNumber,
    isValidSymbols,
  ]);

  return (
    <Tooltip
      placement={EPosition.RIGHT}
      width={'344px'}
      align={isDesktop ? EPosition.TOP : undefined}
      on={'focus'}
      widthContainer="100%"
      remove={
        isValidCharacters() &&
        isValidLetter() &&
        isValidCapital() &&
        isValidNumber() &&
        isValidSymbols()
      }
      content={
        <CheckPasswordContent
          isValidCharacters={isValidCharacters()}
          isValidLetter={isValidLetter()}
          isValidCapital={isValidCapital()}
          isValidNumber={isValidNumber()}
          isValidSymbols={isValidSymbols()}
        />
      }
    >
      {children}
    </Tooltip>
  );
};
